import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { AppWindow, Database, FileCheck, FileText, FlaskConical, Hash, Server, Users } from "lucide-react";
import { useProjectServiceUser } from "@lumanity/ui/projects";
import { NavLink, Outlet, useLocation } from "react-router-dom";


const navGroups = [
  {
    heading: "Master Data",
    items: [
      { path: "users", label: "Users", description: "Manage application users", icon: Users },
      { path: "therapy-areas", label: "Therapy Areas", description: "Manage therapy area classifications", icon: FlaskConical },
      { path: "project-codes", label: "Project Codes", description: "Manage project code identifiers", icon: Hash },
      { path: "evidence-types", label: "Evidence Types", description: "Manage evidence type classifications", icon: FileCheck },
      { path: "source-types", label: "Source Types", description: "Manage document source categories", icon: Database },
      {
        path: "supporting-document-types",
        label: "Supporting Document Types",
        description: "Manage the kinds of project reference document",
        icon: FileText,
      },
    ],
  },
  {
    heading: "Access",
    items: [
      {
        path: "project-availability",
        label: "Project Availability",
        description: "Choose which apps each project is available in",
        icon: AppWindow,
      },
      {
        path: "registered-apps",
        label: "Registered Apps",
        description: "Register apps with the project service and choose the optional project sections each one shows",
        icon: Server,
        platformAdminOnly: true,
      },
    ],
  },
];

const navItems = navGroups.flatMap((g) => g.items);

const Admin = () => {
  const { pathname } = useLocation();
  const current = navItems.find((n) => pathname.endsWith(n.path)) ?? navItems[0];
  // Platform-admin pages stay reachable by URL (they show a notice), but only platform admins see them listed.
  const { data: serviceUser } = useProjectServiceUser();
  const visibleGroups = navGroups.map((g) => ({
    ...g,
    items: g.items.filter((item) => !("platformAdminOnly" in item) || serviceUser?.is_platform_admin),
  }));

  return (
    <PageLayout>
      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 flex flex-col gap-1">
          {visibleGroups.map(({ heading, items }, i) => (
            <div key={heading} className={cn("flex flex-col gap-1", i > 0 && "mt-4")}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
                {heading}
              </p>
              {items.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>{current.label}</CardTitle>
              <CardDescription>{current.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Outlet />
            </CardContent>
          </Card>
        </main>
      </div>
    </PageLayout>
  );
};

export default Admin;
